import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
    Alert,
    Modal,
    Table
} from "react-bootstrap";
import {
    Link
} from "react-router-dom";

import ShopsContext from "../../../../context/ShopsContext";
import Loading from "../../../../components/Loading";
import DateFormatter from "../../../../components/DateFormatter";

function CourseOpeningSummaryShopUsersModal({ show, handleClose, course, initialSection }) {
    const shopsContext = useContext(ShopsContext);
    const [courseOpeningSummaryShopUsers, setCourseOpeningSummaryShopUsers] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if(!show || !course) {
            setCourseOpeningSummaryShopUsers(null);
            setError(null);
            return () => {
                cancelled = true;
            };
        }
        const loadCourseOpeningSummaryShopUsers = async () => {
            setCourseOpeningSummaryShopUsers(null);
            setError(null);
            try {
                const response = await axios.post("/getCourseOpeningSummaryShopUsers", {
                    shopId: shopsContext.currentShop.id,
                    courseId: course.id
                });
                if(cancelled) {
                    return;
                }
                if(response.data.valid) {
                    setCourseOpeningSummaryShopUsers(response.data.courseOpeningSummaryShopUsers);
                } else {
                    setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
                }
            } catch(requestError) {
                if(cancelled) {
                    return;
                }
                console.error(requestError);
                setError("Er ging iets fout. Probeer het later opnieuw.");
            }
        };
        loadCourseOpeningSummaryShopUsers();
        return () => {
            cancelled = true;
        };
    }, [course, show, shopsContext.currentShop.id]);

    const shopUserBaseUrl = useMemo(() => {
        return "/shop/" + shopsContext.currentShop.codeName + "/shop-user/";
    }, [shopsContext.currentShop.codeName]);

    const orderedSections = useMemo(() => {
        const startedSection = {
            key: "started",
            title: "Gestart met cursus",
            emptyMessage: "Nog niemand is met deze cursus gestart.",
            shopUsers: courseOpeningSummaryShopUsers?.startedShopUsers || []
        };
        const neverStartedSection = {
            key: "never-started",
            title: "Nooit gestart",
            emptyMessage: "Iedereen met toegang is al met deze cursus gestart.",
            shopUsers: courseOpeningSummaryShopUsers?.neverStartedShopUsers || []
        };

        if(initialSection === "never-started") {
            return [neverStartedSection, startedSection];
        }
        return [startedSection, neverStartedSection];
    }, [courseOpeningSummaryShopUsers, initialSection]);

    return (
        <Modal show={ show } onHide={ handleClose } size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    Cursusgebruikers{ courseOpeningSummaryShopUsers?.courseTitle ? ": " + courseOpeningSummaryShopUsers.courseTitle : course?.title ? ": " + course.title : "" }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { error && (
                    <Alert variant="danger">{ error }</Alert>
                )}
                { !error && !courseOpeningSummaryShopUsers && (
                    <Loading/>
                )}
                { courseOpeningSummaryShopUsers && (
                    <React.Fragment>
                        <div className="card course-statistics-summary mb-4">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4 mb-3 mb-md-0">
                                        <div className="course-statistics-summary__item">
                                            <small className="text-muted d-block">Toegang gehad</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseOpeningSummaryShopUsers.amountOfShopUsersThatEverHadAccess }
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3 mb-md-0">
                                        <div className="course-statistics-summary__item">
                                            <small className="text-muted d-block">Gestart met cursus</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseOpeningSummaryShopUsers.amountOfShopUsersThatStartedCourse }
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className={ "course-statistics-summary__item" + (courseOpeningSummaryShopUsers.amountOfShopUsersThatNeverStartedCourse > 0 ? " course-statistics-summary__item--warning" : "") }>
                                            <small className="text-muted d-block">Nooit gestart</small>
                                            <strong className="course-statistics-summary__count">
                                                { courseOpeningSummaryShopUsers.amountOfShopUsersThatNeverStartedCourse }
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-muted mb-0 mt-3">
                                    Deze overzichten tonen de gebruikers achter de cursus-samenvatting.
                                    Lesstatistieken gebruiken alleen gebruikers die met de cursus gestart zijn.
                                </p>
                            </div>
                        </div>

                        { orderedSections.map((section) => (
                            <React.Fragment key={ section.key }>
                                <h4>{ section.title }</h4>
                                { section.shopUsers.length === 0 ? (
                                    <p className="text-muted">
                                        { section.emptyMessage }
                                    </p>
                                ) : (
                                    <Table size="sm" hover className="mb-4">
                                        <thead className="thead-light">
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-left">Naam</th>
                                            <th className="text-left">Email</th>
                                            <th className="text-center">Toegang</th>
                                            <th className="text-right">Toegang sinds</th>
                                            <th className="text-right">Verloopdatum</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        { section.shopUsers.map((shopUser) => {
                                            const url = shopUserBaseUrl + shopUser.id;
                                            return (
                                                <tr key={ shopUser.id } className="table-clickable-row">
                                                    <th className="text-center">
                                                        <Link to={ url }>
                                                            { shopUser.id }
                                                        </Link>
                                                    </th>
                                                    <td className="text-left">
                                                        <Link to={ url }>
                                                            { shopUser.name }
                                                        </Link>
                                                    </td>
                                                    <td className="text-left">
                                                        <Link to={ url }>
                                                            { shopUser.email }
                                                        </Link>
                                                    </td>
                                                    <td className="text-center">
                                                        <Link to={ url }>
                                                            { shopUser.courseAccess?.hasAccess ? (
                                                                <i className="fa-solid fa-check fa-fw text-success"/>
                                                            ) : (
                                                                <i className="fa-solid fa-times fa-fw text-danger"/>
                                                            )}
                                                        </Link>
                                                    </td>
                                                    <td className="text-right">
                                                        <Link to={ url }>
                                                            { shopUser.courseAccess?.firstAccessDate ? (
                                                                <DateFormatter date={ shopUser.courseAccess.firstAccessDate }/>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </Link>
                                                    </td>
                                                    <td className="text-right">
                                                        <Link to={ url }>
                                                            { shopUser.courseAccess?.expirationDate ? (
                                                                <DateFormatter date={ shopUser.courseAccess.expirationDate }/>
                                                            ) : (
                                                                "Nooit"
                                                            )}
                                                        </Link>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </Table>
                                )}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                )}
            </Modal.Body>
        </Modal>
    );
}
CourseOpeningSummaryShopUsersModal.propTypes = {
    show: PropTypes.bool,
    handleClose: PropTypes.func.isRequired,
    course: PropTypes.object,
    initialSection: PropTypes.string
}

export default React.memo(CourseOpeningSummaryShopUsersModal);
